import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useQuery, useMutation } from '@apollo/client';

// material
import { LoadingButton } from '@mui/lab';
import { Box, Stack, IconButton, InputAdornment, InputLabel, FormControl } from '@mui/material';

// graphql
import { UserGQLFunction } from '../../graphql/USER';
// hooks
import useResponsive from '../../hooks/useResponsive';
// components
import { FormProvider, RHFTextField, RHFToggleButton, RHFSelect } from '../../components/hook-form';

// info
import { USER } from '../../info/USER';

export default function EditUserForm({ id }) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const smUp = useResponsive('up', 'sm');
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const EditUserSchema = Yup.object().shape({
    userId: Yup.string().required(t('people.userid_req')),
    email: Yup.string().email(t('login.email_valid')).required(t('people.email_req')),
    password: Yup.string(),
    confirmPassword: Yup.string().oneOf([Yup.ref('password'), null], t('login.password_match')),
    username: Yup.string().required(t('people.username_req')),
  });

  const defaultValues = {
    id: '',
    userId: '',
    email: '',
    password: '',
    username: '',
    nickname: '',
    title: '',
    userType: '',
    userState: 0,
  };

  const methods = useForm({
    resolver: yupResolver(EditUserSchema),
    defaultValues,
  });
  const {
    handleSubmit,
    formState: { isSubmitting },
    reset,
    setError,
  } = methods;

  const { loading: queryLoading, data: queryData } = useQuery(UserGQLFunction.GET_USER_INFO, {
    variables: { id },
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (queryData && queryData.userInfo) {
      const info = queryData.userInfo;
      reset({
        id: info.id || '',
        userId: info.userId || '',
        email: info.email || '',
        password: '',
        username: info.username || '',
        nickname: info.nickname || '',
        title: info.title || '',
        userType: info.userType || 0,
        userState: info.userState || 0,
      });
    }
  }, [queryData, reset]);

  const [updateUser] = useMutation(UserGQLFunction.UPDATE_USER);

  const onSubmit = async (input) => {
    try {
      const { data } = await updateUser({
        variables: {
          id,
          email: input.email,
          username: input.username,
          nickname: input.nickname,
          title: input.title,
          userType: input.userType,
          userState: input.userState,
        },
      });

      const info = data.updateUser;
      if (info.err) {
        setError(info.err, { type: 'custom', message: info.msg });
      } else {
        enqueueSnackbar(t('common.success'), { variant: 'success' });
        navigate(`/user/info/${id}`, { replace: true });
      }
    } catch (error) {
      enqueueSnackbar(error.message, { variant: 'error' });
    }
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Box
        sx={{
          display: 'grid',
          columnGap: 2,
          rowGap: 3,
          gridTemplateColumns: smUp ? 'repeat(2, 1fr)' : '1fr',
          mt: 1,
          mb: 4,
        }}>
        <RHFTextField name="id" label={t('common.id')} disable loading={queryLoading} />
        <RHFTextField name="userId" disabled label={t('common.user_id')} loading={queryLoading} />
        <RHFTextField name="email" label={t('common.email')} loading={queryLoading} />
        <RHFTextField name="username" label={t('common.username')} loading={queryLoading} />
        <RHFTextField name="nickname" label={t('common.nickname')} loading={queryLoading} />
        <RHFTextField name="title" label={t('common.title')} loading={queryLoading} />

        {smUp ? (
          <FormControl sx={{ gridColumn: 'span 2' }}>
            <Box sx={{ mb: 2 }}>
              <InputLabel shrink>{t('common.user_state')}</InputLabel>
            </Box>
            <RHFToggleButton states={USER.USER_STATE.map(state => t(`common.${state.toLowerCase()}`))} name="userState" loading={queryLoading} />
          </FormControl>
        ) : (
          <FormControl>
            <RHFSelect label={t('common.user_state')} states={USER.USER_STATE.map(state => t(`common.${state.toLowerCase()}`))} name="userState" loading={queryLoading} />
          </FormControl>
        )}
        {smUp ? (
          <FormControl sx={{ gridColumn: 'span 2' }}>
            <Box sx={{ mb: 2 }}>
              <InputLabel shrink>{t('common.user_type')}</InputLabel>
            </Box>
            <RHFToggleButton states={USER.USER_TYPE.map(type => t(`common.${type.toLowerCase()}`))} name="userType" loading={queryLoading} />
          </FormControl>
        ) : (
          <FormControl>
            <RHFSelect label={t('common.user_type')} states={USER.USER_TYPE.map(type => t(`common.${type.toLowerCase()}`))} name="userType" loading={queryLoading} />
          </FormControl>
        )}
      </Box>
      <LoadingButton
        sx={{ maxWidth: '200px' }}
        fullWidth
        type="submit"
        variant="contained"
        loading={isSubmitting}>
        {t('common.save')}
      </LoadingButton>
    </FormProvider>
  );
}
